import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './footer.css';
import logo from '../../asserts/logo.png';
import { supabase } from '../../supabaseClient';

gsap.registerPlugin(ScrollTrigger);

const Footer = () => {
  const [lang, setLang] = useState('en');
  const [footerTitles, setFooterTitles] = useState([]);
  const [footerLinks, setFooterLinks] = useState([]);
  const [dataReady, setDataReady] = useState(false);

  const footerRef = useRef();
  const overlayRef = useRef();
  const logoRef = useRef();
  const col1Ref = useRef();
  const col2Ref = useRef();
  const col3Ref = useRef();
  const col4Ref = useRef();
  const newsletterRef = useRef();
  const midRef = useRef();
  const watermarkRef = useRef();
  const watermarkLogoRef = useRef();

  useEffect(() => {
    const fetchAll = async () => {
      const [titlesRes, linksRes] = await Promise.all([
        supabase.from('Footer_title').select('*'),
        supabase.from('Footer_links').select('*'),
      ]);
      setFooterTitles(titlesRes.data || []);
      setFooterLinks(linksRes.data || []);
      setDataReady(true);
    };
    fetchAll();
  }, []);

  useEffect(() => {
    if (!dataReady) return;

    const ctx = gsap.context(() => {

      if (footerRef.current) {
        gsap.fromTo(footerRef.current,
          { y: 80, opacity: 0, borderRadius: '200px 200px 0 0' },
          {
            y: 0, opacity: 1, borderRadius: '150px 150px 0 0',
            duration: 1.2, ease: 'expo.out',
            scrollTrigger: { trigger: footerRef.current, start: 'top 95%' }
          }
        );
      }

      if (logoRef.current) {
        gsap.fromTo(logoRef.current,
          { scale: 0, opacity: 0, rotation: -180 },
          {
            scale: 1, opacity: 1, rotation: 0,
            duration: 1, ease: 'elastic.out(1, 0.5)',
            scrollTrigger: { trigger: footerRef.current, start: 'top 88%' }
          }
        );
      }

      const colEls = [col1Ref.current, col2Ref.current, col3Ref.current, col4Ref.current].filter(Boolean);
      if (colEls.length) {
        gsap.fromTo(colEls,
          { y: 50, opacity: 0, filter: 'blur(8px)' },
          {
            y: 0, opacity: 1, filter: 'blur(0px)',
            duration: 0.7, stagger: 0.12, ease: 'power3.out',
            scrollTrigger: { trigger: col1Ref.current, start: 'top 92%' }
          }
        );
      }

      gsap.fromTo('.footer__link',
        { x: -20, opacity: 0 },
        {
          x: 0, opacity: 1,
          duration: 0.5, stagger: 0.06, ease: 'power2.out',
          scrollTrigger: { trigger: col1Ref.current || footerRef.current, start: 'top 90%' }
        }
      );

      if (newsletterRef.current) {
        gsap.fromTo(newsletterRef.current,
          { x: 60, opacity: 0 },
          {
            x: 0, opacity: 1,
            duration: 0.9, ease: 'expo.out',
            scrollTrigger: { trigger: newsletterRef.current, start: 'top 92%' }
          }
        );

        const input = newsletterRef.current.querySelector('.footer__newsletter-input');
        if (input) {
          input.addEventListener('focus', () => {
            gsap.to(input.parentElement, { boxShadow: '0 0 0 2px rgba(255,30,30,0.4)', duration: 0.3 });
          });
          input.addEventListener('blur', () => {
            gsap.to(input.parentElement, { boxShadow: '0 0 0 0px rgba(255,30,30,0)', duration: 0.3 });
          });
        }

        const btn = newsletterRef.current.querySelector('.footer__newsletter-btn');
        if (btn) {
          btn.addEventListener('mouseenter', () => {
            gsap.to(btn, { scaleX: 1.06, scaleY: 1.04, duration: 0.25, ease: 'back.out(2)' });
          });
          btn.addEventListener('mouseleave', () => {
            gsap.to(btn, { scaleX: 1, scaleY: 1, duration: 0.35, ease: 'elastic.out(1, 0.4)' });
          });
        }
      }

      if (midRef.current) {
        gsap.fromTo(midRef.current,
          { opacity: 0, y: 20 },
          {
            opacity: 1, y: 0, duration: 0.8, ease: 'power2.out',
            scrollTrigger: { trigger: midRef.current, start: 'top 98%' }
          }
        );
      }

      if (watermarkLogoRef.current) {
        gsap.fromTo(watermarkLogoRef.current,
          { x: -60, opacity: 0 },
          {
            x: 0, opacity: 0.8, duration: 1.2, ease: 'expo.out',
            scrollTrigger: { trigger: watermarkRef.current, start: 'top 98%' }
          }
        );

        gsap.to(watermarkLogoRef.current, {
          x: 30,
          ease: 'none',
          scrollTrigger: {
            trigger: footerRef.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 2,
          }
        });
      }

      document.querySelectorAll('.footer__link, .footer__legal-link').forEach(link => {
        link.addEventListener('mouseenter', () => {
          gsap.to(link, { x: 6, duration: 0.25, ease: 'power2.out' });
        });
        link.addEventListener('mouseleave', () => {
          gsap.to(link, { x: 0, duration: 0.35, ease: 'elastic.out(1, 0.5)' });
        });
      });

    });

    return () => ctx.revert();
  }, [dataReady]);

  const linkGroups = [[], [], [], []];
  footerLinks.forEach((link) => {
    if (link.id <= 4) linkGroups[0].push(link);
    else if (link.id <= 7) linkGroups[1].push(link);
    else if (link.id <= 9) linkGroups[2].push(link);
    else linkGroups[3].push(link);
  });

  const colRefs = [col1Ref, col2Ref, col3Ref, col4Ref];

  return (
    <footer className="footer gsap-hidden" ref={footerRef}>
      <div className="footer__overlay" ref={overlayRef}></div>


      <div className="footer__inner">

        <div className="footer__top-logo gsap-hidden" ref={logoRef}>
        </div>

        <div className="footer__grid">

          {footerTitles.map((title, index) => (
            <div className="footer__col gsap-hidden" ref={colRefs[index]} key={title.id}>
              <h4 className="footer__col-title">{title.category_en}</h4>
              <nav className="footer__links" aria-label={`${title.category_en} links`}>
                {(linkGroups[index] || []).map((link) => (
                  <a className="footer__link" href="#" key={link.id}>{link.footer_links_en}</a>
                ))}
              </nav>
            </div>
          ))}

          <div className="footer__col footer__col--newsletter gsap-hidden" ref={newsletterRef}>
            <p className="footer__lang-label" id="footer-lang-label">LANGUAGE</p>
            <div className={`footer__lang${lang === 'ar' ? ' footer__lang--ar' : ''}`} role="group" aria-labelledby="footer-lang-label">
              <div className="footer__lang-slider" />
              <button
                className={`footer__lang-btn${lang === 'en' ? ' footer__lang-btn--active' : ''}`}
                onClick={() => setLang('en')}
                aria-pressed={lang === 'en'}
              >EN</button>
              <button
                className={`footer__lang-btn${lang === 'ar' ? ' footer__lang-btn--active' : ''}`}
                onClick={() => setLang('ar')}
                aria-pressed={lang === 'ar'}
              >AR</button>
            </div>
            <h4 className="footer__col-title">Our newsletter on Design &amp; Architecture</h4>
            <form className="footer__newsletter" onSubmit={(e) => e.preventDefault()}>
              <input
                id="newsletter-email"
                className="footer__newsletter-input"
                type="email"
                placeholder="Your email address"
                required
              />
              <button className="footer__newsletter-btn" type="submit">Register now</button>
            </form>
          </div>

        </div>

        <div className="footer__mid gsap-hidden" ref={midRef}>
          <span className="footer__address">Cairo Exhibition Center – Nasr City, Cairo, Egypt</span>
          <div className="footer__legal">
            <a className="footer__legal-link" href="#">Privacy Policy</a>
            <a className="footer__legal-link" href="#">Terms of Service</a>
          </div>
        </div>

      </div>

      <div className="footer__watermark" ref={watermarkRef}>
        <img
          src={logo}
          alt="The Design Show"
          className="footer__watermark-logo gsap-hidden"
          ref={watermarkLogoRef}
        />
      </div>
    </footer>
  );
};

export default Footer;